import localforage from 'localforage';
import { getSessionDekAsCryptoKey } from './keyManager';

// Gate: redux-persist rehydrates before AuthProvider restores the encryption
// key. Skip decrypt attempts until the key has been properly set up.
let _storageReady = false;

/** Call after the DEK has been placed in sessionStorage by keyManager. */
export function markEncryptedStorageReady() {
  _storageReady = true;
}

/**
 * Get the current session DEK as a CryptoKey (for use outside the adapter).
 * Delegates to keyManager — kept as a named export for migrateLegacyData.js.
 */
export async function getEncryptionKeyForDb() {
  return getSessionDekAsCryptoKey();
}

// Encrypt data with AES-GCM
async function encrypt(key, data) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(data));
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);
  return btoa(String.fromCharCode(...combined));
}

// Decrypt data
async function decrypt(key, encryptedString) {
  try {
    const combined = Uint8Array.from(atob(encryptedString), c => c.charCodeAt(0));
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, encrypted);
    return JSON.parse(new TextDecoder().decode(decrypted));
  } catch (e) {
    console.warn('Failed to decrypt data:', e);
    return null;
  }
}

function isEncrypted(data) {
  return typeof data === 'string';
}

/**
 * Encrypted storage adapter for redux-persist.
 *
 * Migration strategy for existing unencrypted data:
 * 1. If data exists and is unencrypted (object), return it as-is (allows app to load)
 * 2. When user logs in and setItem is called, data gets encrypted
 * 3. If data is encrypted but no key available, return null (user must log in)
 */
const encryptedStorage = {
  async getItem(key) {
    const rawData = await localforage.getItem(key);
    if (!rawData) return null;

    if (!isEncrypted(rawData)) {
      return JSON.stringify(rawData);
    }

    if (!_storageReady) {
      return null;
    }

    const encryptionKey = await getSessionDekAsCryptoKey();
    if (!encryptionKey) return null;

    const decrypted = await decrypt(encryptionKey, rawData);
    return decrypted ? JSON.stringify(decrypted) : null;
  },

  async setItem(key, value) {
    const encryptionKey = await getSessionDekAsCryptoKey();
    if (!encryptionKey) {
      console.log('No encryption key, storing unencrypted (will encrypt after login)');
      const data = JSON.parse(value);
      await localforage.setItem(key, data);
      return;
    }

    const data = JSON.parse(value);
    const encrypted = await encrypt(encryptionKey, data);
    await localforage.setItem(key, encrypted);
  },

  async removeItem(key) {
    await localforage.removeItem(key);
  }
};

export default encryptedStorage;
